import { useEffect, useState, type ReactNode } from 'react';
import { useSession, type RolApp } from '../hooks/useSession';
import { useMfaStatus } from '../hooks/useMfaStatus';
import { useAllowedEmail, useIsSuperAdmin, type EmailScope } from '../hooks/useAllowedEmail';
import { usePasswordOnboarding } from '../hooks/usePasswordOnboarding';
import { MfaUnlockModal } from './MfaUnlockModal';
import { SetPasswordModal } from './SetPasswordModal';
import { getSupabase } from '../client';

interface AuthGateProps {
  /** Roles permitidos. Si vacío, basta con estar autenticado. */
  allowed?: RolApp[];
  /**
   * Scope del panel donde está montado el AuthGate. Si se setea, el gate
   * verifica que el row del usuario en `allowed_emails` coincida y esté
   * activo. Sin el panelScope se preserva el comportamiento antiguo (solo
   * verifica rol_app vía `allowed`).
   *   - 'aosenuma'  → equipo / staff (elawan-equipo.aosenumareport.com)
   *   - 'cliente'   → panel cliente (slug.aosenumareport.com)
   */
  panelScope?: EmailScope;
  /** Si true, exige aal2 (MFA reciente). Default false. */
  requireAal2?: boolean;
  /**
   * Si true, fuerza enrollment TOTP cuando el user no tiene factor verified —
   * SALVO que su fila en `allowed_emails` tenga `totp_required=false` (toggle
   * data-driven del panel Accesos). Los super-admins ignoran el toggle y
   * SIEMPRE requieren TOTP (piso de seguridad).
   */
  forceEnroll?: boolean;
  /**
   * TTL (ms) del último challenge TOTP. Cuando expira, abre MfaUnlockModal
   * automáticamente sobre los children — sin logout. Cancel = signOut.
   * Default: undefined (sin TTL — la sesión queda aal2 hasta logout).
   *
   * Uso típico: equipo aosenuma con `mfaTtlMs={8 * 60 * 60 * 1000}` (8h)
   * para forzar re-MFA al inicio de cada jornada.
   */
  mfaTtlMs?: number;
  /** Componente mientras carga. */
  fallback?: ReactNode;
  /** Si no autenticado: redirect href o componente custom. */
  unauthenticated?: ReactNode | string;
  /** Si autenticado pero rol no permitido. */
  forbidden?: ReactNode;
  /** Si requireAal2 y aún no aal2. */
  needsMfa?: ReactNode;
  children: ReactNode;
}

/**
 * Gate de auth + rol + aal. Wrappear rutas/páginas sensibles.
 *
 * Ejemplo:
 *   <AuthGate allowed={['admin_aosenuma','tecnico_aosenuma']}>
 *     <CapturaPage />
 *   </AuthGate>
 *
 *   <AuthGate requireAal2 forbidden={<MfaEnrollPrompt />}>
 *     <PiiSensitiveSection />
 *   </AuthGate>
 */
export function AuthGate({
  allowed,
  panelScope,
  requireAal2 = false,
  forceEnroll = false,
  mfaTtlMs,
  fallback,
  unauthenticated,
  forbidden,
  needsMfa,
  children,
}: AuthGateProps): ReactNode {
  const { user, rolApp, isAal2, mfaTimestamp, loading } = useSession();
  const mfa = useMfaStatus();
  // Cargar el row de allowed_emails solo cuando panelScope está seteado
  // (apps que aún no migraron al modelo de whitelist seguirán funcionando
  // sin este check, solo con `allowed` por rol_app).
  const allowedEmail = useAllowedEmail();
  // Super-admin override: los 3 emails hardcoded entran a CUALQUIER panel
  // (equipo + cualquier slug.aosenumareport.com de cliente). Bypass de
  // panelScope + allowed. Diego/Saúl/Javier son los responsables del piloto
  // y necesitan ver tanto el panel del equipo como el del cliente.
  const isSuperAdmin = useIsSuperAdmin();
  // Bootstrap del row public.usuarios + refresh JWT. CRÍTICO ejecutar este
  // hook al top-level (no dentro de PasswordOnboardingGate como antes) para
  // que un user nuevo (sin row, sin rol_app en JWT) NO tropiece con el
  // check de `allowed` antes de que el bootstrap complete.
  const onboarding = usePasswordOnboarding();

  // Tick cada 60s para re-evaluar el TTL aunque el user no interactúe.
  // Solo se activa si mfaTtlMs está seteado y el user ya está aal2.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!mfaTtlMs || !isAal2) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [mfaTtlMs, isAal2]);

  // Default fallback: spinner visible (no `null`). Antes el blanco era confuso —
  // tenant podia creer que la app no carga cuando solo era checking session.
  if (loading || mfa.loading) return fallback ?? <DefaultLoading />;

  if (!user) {
    if (typeof unauthenticated === 'string') {
      if (typeof window !== 'undefined') window.location.href = unauthenticated;
      return null;
    }
    return unauthenticated ?? null;
  }

  // Whitelist + panel-scope va PRIMERO cuando está activo: si el user es
  // staff aosenuma intentando entrar al panel cliente (o viceversa), el
  // check de `allowed` por rol_app produciría un genérico "Sin permisos"
  // y el user no sabría que su panel correcto está en otro subdominio.
  // Evaluando panelScope primero podemos mostrar "Panel equivocado" con
  // un botón "Ir a mi panel" que redirige automáticamente.
  //
  // Super-admin bypass: si el user está en la lista de los 3 hardcoded,
  // saltamos completamente las verificaciones de panel y rol. Pueden
  // entrar a cualquier subdominio (equipo + cualquier cliente).
  if (panelScope && !isSuperAdmin) {
    if (allowedEmail.loading) return fallback ?? <DefaultLoading />;
    const row = allowedEmail.row;
    // row=undefined → email no está en la whitelist (autorizado pero NO listado).
    if (row === undefined) {
      return forbidden ?? <DefaultForbiddenScope reason="no_whitelist" />;
    }
    if (allowedEmail.error || !row) {
      // Error de RED/RLS. Antes mostrábamos `<DefaultLoading />` que
      // dejaba al user atrapado en "Verificando sesión…" sin escape.
      // Ahora mostramos un panel con el error y opción de cerrar sesión
      // (la causa común es policy RLS rota o JWT corrupto — signOut + retry
      // resuelve la mayoría de los casos). Mantiene visibilidad para debug.
      return forbidden ?? <DefaultForbiddenScope reason="error" detail={allowedEmail.error?.message} />;
    }
    if (row.status === 'suspended') {
      return forbidden ?? <DefaultForbiddenScope reason="suspended" />;
    }
    if (row.scope !== panelScope) {
      return forbidden ?? <DefaultForbiddenScope reason="wrong_panel" expected={panelScope} actual={row.scope} />;
    }
  }

  // Si el user pasó panelScope (existe en allowed_emails active) pero el
  // JWT aún no tiene rol_app, esperamos a que el bootstrap+refresh termine.
  // Sin esto, el primer login de un user nuevo (vía magic link) cae al
  // check de `allowed` con rolApp=null → "Sin permisos" mientras el row
  // se está creando en background.
  const needsBootstrap = !!user && !rolApp && !isSuperAdmin && (allowedEmail.row != null);
  if (needsBootstrap && (onboarding.loading || onboarding.bootstrapped)) {
    return fallback ?? <DefaultLoading />;
  }

  // Super-admin también pasa el check de `allowed` (defensa secundaria
  // por si el rol_app del JWT no está actualizado tras un cambio).
  if (!isSuperAdmin && allowed && allowed.length > 0 && (!rolApp || !allowed.includes(rolApp))) {
    // Si onboarding está activo (loading o bootstrapped recientemente),
    // damos un margen extra mostrando loading. El JWT debería actualizarse
    // pronto vía onAuthStateChange tras refreshSession.
    if (onboarding.loading) return fallback ?? <DefaultLoading />;
    return forbidden ?? <DefaultForbidden />;
  }

  // Decisión MFA unificada — siempre vía MfaUnlockModal:
  //   - El modal hace listFactors fresco al montar y decide enroll vs
  //     challenge. NO dependemos de useMfaStatus.enrolled (cache local de
  //     30s que se corrompe a `false` cuando hay Web Lock contention en
  //     auth-js → listFactors timeout 3s → AuthGate intenta enroll → server
  //     responde 403 "AAL2 required to enroll a new factor" si el user ya
  //     tenía factor verified). isAal2 viene del JWT, fresco siempre.
  //
  //   - forceEnroll: prompt si la sesión NO está en aal2 (sin importar si
  //     el factor existe — el modal detecta server-side y elige enroll o
  //     challenge según corresponda).
  //   - mfaTtlMs: re-MFA cuando ya estás en aal2 pero el último challenge
  //     TOTP venció (jornada > N horas).
  // Exención TOTP data-driven (toggle del panel Accesos): si la fila de
  // allowed_emails del user tiene totp_required=false, NO disparamos enroll ni
  // re-MFA por TTL — entra con solo contraseña/magic link (aal1). Los
  // super-admins ignoran el toggle y SIEMPRE requieren TOTP (piso de seguridad).
  const mfaExempt = !isSuperAdmin && allowedEmail.row?.totp_required === false;
  const needsForceUpgrade = forceEnroll && !isAal2 && !mfaExempt;
  const ttlExpired =
    !mfaExempt &&
    !!mfaTtlMs &&
    isAal2 &&
    (mfaTimestamp == null || Date.now() - mfaTimestamp > mfaTtlMs);

  if (needsForceUpgrade || ttlExpired) {
    return (
      <>
        {children}
        <MfaUnlockModal
          cancelLabel="Cerrar sesión"
          onSuccess={() => {
            mfa.refresh();
            // Forzar re-render — useSession captará el nuevo JWT vía
            // onAuthStateChange tras mfa.verify.
            setTick((t) => t + 1);
          }}
          onCancel={() => {
            // CRÍTICO orden: 1) limpiar localStorage manualmente (instantáneo,
            // sin lock), 2) disparar signOut fire-and-forget para invalidar
            // server-side, 3) navegar inmediato. Antes intentábamos `await
            // signOut()` antes de navegar pero auth-js usa un Web Lock interno
            // ("lock:sb-...-auth-token") que puede estar retenido por otro
            // componente — visto en logs: "Lock was not released within
            // 5000ms". Si signOut cuelga esperando ese lock, await nunca
            // termina y "Cerrar sesión" parece no hacer nada. Limpiar
            // localStorage primero garantiza que aunque navegamos sin esperar,
            // un futuro mount de AuthGate no encontrará sesión válida.
            if (typeof window !== 'undefined') {
              try {
                Object.keys(window.localStorage)
                  .filter((k) => k.startsWith('sb-'))
                  .forEach((k) => window.localStorage.removeItem(k));
              } catch { /* QuotaExceeded o storage corrupto */ }
            }
            void getSupabase().auth.signOut().catch(() => undefined);
            if (typeof window !== 'undefined') {
              if (typeof unauthenticated === 'string') {
                window.location.href = unauthenticated;
              } else {
                window.location.reload();
              }
            }
          }}
        />
      </>
    );
  }

  if (requireAal2 && !isAal2) {
    return needsMfa ?? <DefaultNeedsMfa />;
  }

  // Onboarding final: el user pasó MFA pero aún no creó password permanente.
  // Esto pasa SOLO en el primer login post magic-link. Los renders siguientes
  // ven password_set=true (lo seteamos por SQL backfill + el RPC tras submit).
  // El hook `onboarding` ya está cargado al top-level del AuthGate; aquí solo
  // decidimos si renderizar el modal según su estado actual.
  const showSetPassword = onboarding.passwordSet === false && !onboarding.loading && !onboarding.error;
  return (
    <>
      {children}
      {showSetPassword && (
        <SetPasswordModal
          tempPasswordMode
          onSuccess={() => {
            void onboarding.refresh();
          }}
        />
      )}
    </>
  );
}

function DefaultLoading(): ReactNode {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-deep, #0c1216)',
        color: 'var(--text-mid, rgba(255,255,255,0.78))',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 28,
            height: 28,
            margin: '0 auto 14px',
            border: '2px solid rgba(255,255,255,0.12)',
            borderTopColor: 'var(--btn-primary-bg, #b4c96f)',
            borderRadius: '50%',
            animation: 'aosenuma-spin 0.8s linear infinite',
          }}
        />
        <p style={{ margin: 0, fontSize: 13 }}>Verificando sesión…</p>
      </div>
    </div>
  );
}

if (typeof document !== 'undefined' && !document.getElementById('aosenuma-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'aosenuma-spin-kf';
  style.textContent = '@keyframes aosenuma-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

function DefaultForbidden(): ReactNode {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui' }}>
      <h2>Sin permisos</h2>
      <p>Tu cuenta no tiene acceso a esta sección.</p>
      <button
        onClick={() => {
          void getSupabase().auth.signOut();
          if (typeof window !== 'undefined') window.location.href = '/';
        }}
      >
        Cerrar sesión
      </button>
    </div>
  );
}

function DefaultNeedsMfa(): ReactNode {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui' }}>
      <h2>Verificación adicional</h2>
      <p>Esta sección requiere autenticación reforzada (MFA reciente).</p>
      <p>Por favor cierra sesión y vuelve a entrar verificando tu segundo factor.</p>
    </div>
  );
}

interface DefaultForbiddenScopeProps {
  reason: 'no_whitelist' | 'suspended' | 'wrong_panel' | 'error';
  expected?: EmailScope;
  actual?: EmailScope;
  detail?: string;
}

/**
 * URL del panel correcto cuando el user entró al equivocado. Lee de
 * VITE_PUBLIC_URL_EQUIPO / VITE_PUBLIC_URL_DASHBOARD que ya están
 * configurados en cada SPA (.env.production.local).
 */
function targetPanelUrl(scope: EmailScope): string | null {
  const env = (typeof import.meta !== 'undefined' && import.meta.env
    ? (import.meta.env as unknown as Record<string, string | undefined>)
    : {}
  );
  if (scope === 'aosenuma') return env.VITE_PUBLIC_URL_EQUIPO ?? null;
  if (scope === 'cliente')  return env.VITE_PUBLIC_URL_DASHBOARD ?? null;
  return null;
}

function DefaultForbiddenScope({ reason, expected, actual, detail }: DefaultForbiddenScopeProps): ReactNode {
  let title = 'Acceso restringido';
  let body = 'Tu correo no está autorizado para esta plataforma.';
  const cta = 'Cerrar sesión';
  // Cuando es wrong_panel y conocemos el panel correcto del user, ofrecemos
  // un CTA primario para llevarlo allí directamente. NO hacemos signOut —
  // la sesión sigue siendo válida en el otro subdominio (mismo Supabase
  // project, JWT en localStorage compartido por dominio padre).
  let goToUrl: string | null = null;
  let goToLabel: string | null = null;
  if (reason === 'suspended') {
    title = 'Cuenta suspendida';
    body = 'Tu acceso fue suspendido. Contacta al administrador para reactivarlo.';
  } else if (reason === 'wrong_panel') {
    title = 'Panel equivocado';
    if (expected === 'aosenuma' && actual === 'cliente') {
      body = 'Esta es la plataforma del equipo Aosenuma. Tu cuenta es de cliente — usa el panel de tu organización.';
      goToUrl = targetPanelUrl('cliente');
      goToLabel = 'Ir al panel cliente';
    } else if (expected === 'cliente' && actual === 'aosenuma') {
      body = 'Este panel es para clientes. Tu cuenta de equipo Aosenuma debe usar el panel del equipo.';
      goToUrl = targetPanelUrl('aosenuma');
      goToLabel = 'Ir al panel del equipo';
    } else {
      body = 'Tu cuenta no corresponde a este panel.';
    }
  } else if (reason === 'error') {
    title = 'Error verificando autorización';
    body = `No pudimos confirmar tus permisos. Cierra sesión y vuelve a entrar; si el problema persiste, contacta al administrador.${detail ? ` (Detalle: ${detail})` : ''}`;
  }
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui', maxWidth: 520, margin: '10vh auto', textAlign: 'center' }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ color: 'rgba(255,255,255,0.7)' }}>{body}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
        {goToUrl && goToLabel && (
          <button
            onClick={() => {
              if (typeof window !== 'undefined') window.location.href = goToUrl;
            }}
            style={{
              padding: '8px 16px',
              background: 'var(--btn-primary-bg, #b4c96f)',
              color: '#0c1216',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {goToLabel} →
          </button>
        )}
        <button
          onClick={() => {
            if (typeof window !== 'undefined') {
              try {
                Object.keys(window.localStorage)
                  .filter((k) => k.startsWith('sb-'))
                  .forEach((k) => window.localStorage.removeItem(k));
              } catch { /* ignore */ }
            }
            void getSupabase().auth.signOut().catch(() => undefined);
            if (typeof window !== 'undefined') window.location.href = '/';
          }}
          style={{
            padding: '8px 16px',
            background: goToUrl ? 'transparent' : 'var(--btn-primary-bg, #b4c96f)',
            color: goToUrl ? 'rgba(255,255,255,0.78)' : '#0c1216',
            border: goToUrl ? '1px solid rgba(255,255,255,0.18)' : 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
